import React, { useState, useEffect, useRef } from 'react';
import Button from './Button';
import Input from './Input';
import DateRangePicker from './DateRangePicker';
import Select from './Select';

const PageHeader = ({ 
  addButton,
  downloadButton,
  filters,
  customActions 
}) => {
  const [localSearchValue, setLocalSearchValue] = useState('');
  const debounceTimerRef = useRef(null);

  // Initialize local search value from filters
  useEffect(() => {
    if (filters?.search?.value !== undefined) {
      setLocalSearchValue(filters.search.value);
    }
  }, [filters?.search?.value]);

  // Debounced search handler
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearchValue(value);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced call
    debounceTimerRef.current = setTimeout(() => {
      if (filters?.search?.onChange) {
        filters.search.onChange(e);
      }
    }, 500); // 500ms debounce delay
  };

  // Clear search handler
  const handleClearSearch = () => {
    setLocalSearchValue('');
    if (filters?.search?.onChange) {
      // Create a synthetic event for clearing
      const clearEvent = {
        target: { value: '' }
      };
      filters.search.onChange(clearEvent);
    }
  };

  // If no props provided, don't render anything
  if (!addButton && !downloadButton && !filters && !customActions) {
    return null;
  }

  return (
    <div className="relative z-30 bg-white border-b border-gray-200 shadow-sm flex justify-between items-start gap-3 sm:gap-4 flex-wrap pb-4 mb-1 w-full">
      {/* Filters Section - Left Side */}
      <div className="flex items-start mt-2 gap-3 sm:gap-4 flex-wrap flex-1 min-w-0 w-full sm:w-auto">
        {filters && (
            <div className="flex items-start gap-3 sm:gap-4 flex-wrap w-full sm:w-auto">
            {filters.search && (
              <div className="w-full sm:w-[280px] relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder={filters.search.placeholder || 'Search...'}
                  value={localSearchValue}
                  onChange={handleSearchChange}
                  className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm placeholder-[#9CA3AF]"
                />
                {localSearchValue && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    type="button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            )}
            
            {filters.dateRange && (
              <div className="w-[210px] h-[38px]" >
                <DateRangePicker
                  startDate={filters.dateRange.startDate}
                  endDate={filters.dateRange.endDate}
                  onStartDateChange={filters.dateRange.onStartDateChange}
                  onEndDateChange={filters.dateRange.onEndDateChange}
                  maxDate={new Date().toISOString().split('T')[0]}
                  className="mb-0"
                />
              </div>
            )}
            
            {filters.dropdowns && filters.dropdowns.map((dropdown, index) => (
              // Width comes from the caller as a literal class, never built
              // here: Tailwind only emits CSS for class strings it can find in
              // the source, so a name assembled at runtime produces no rule and
              // the element falls back to w-full and takes a whole row.
              //
              // A caller that wants full width on mobile passes an "sm:"
              // prefixed class; anything unprefixed applies at every size and
              // must not be paired with w-full, or the two fight.
              <div
                key={index}
                className={
                  dropdown.width
                    ? (dropdown.width.startsWith('sm:') ? `w-full ${dropdown.width}` : dropdown.width)
                    : 'w-full sm:w-[180px]'
                }
              >
                <Select
                  value={dropdown.value}
                  onChange={dropdown.onChange}
                  options={dropdown.options || []}
                  placeholder={dropdown.placeholder || 'Select...'}
                  disabled={dropdown.disabled}
                  searchable={dropdown.searchable !== false}
                  className="mb-0"
                  style={{ marginBottom: 0 }}
                />
              </div>
            ))}
            
            {filters.custom && (
              <div className="flex items-center gap-3">
                {filters.custom}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Custom Actions and Add Button - Right Side */}
      {(customActions || downloadButton || addButton) && (
        <div className="flex items-start mt-2 gap-2 flex-shrink-0">
          {/* Custom Actions */}
          {customActions && (
            <div className="flex items-center gap-2">
              {customActions}
            </div>
          )}
          
          {/* Download Button */}
          {downloadButton && (
            <Button
              variant={downloadButton.variant || 'secondary'}
              onClick={downloadButton.onClick}
              disabled={downloadButton.disabled}
              className={`py-1 px-2.5 ${downloadButton.className || ''}`}
              title={downloadButton.label || 'Download'}
            >
              <svg className="w-5 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </Button>
          )}
          
          {/* Add Button */}
          {addButton && (
            <Button
              variant={addButton.variant || 'success'}
              onClick={addButton.onClick}
              disabled={addButton.disabled}
              className="py-1 px-2.5 text-[0.8125rem] leading-[1.125rem]"
            >
              {addButton.icon || '+'} {addButton.label || 'Add'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
